import React from "react";

/**
 * OruclicksAIIcon — Branded AI icon combining the Oruclicks visual identity
 * (gradient ring · C-arc signature · brand dot) with an AI neural-network motif
 * (triangle of connected nodes replacing the original bar-chart).
 *
 * Fully self-contained SVG — includes its own dark background, so use it
 * directly in any container. Set Avatar bgcolor to "transparent" when wrapping.
 */
const OruclicksAIIcon = ({ size = 36, style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <defs>
      {/* Brand gradient — indigo → cyan → emerald (mirrors oruclicks-logo.svg) */}
      <linearGradient id="oru-ai-ring" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%"   stopColor="#6366F1" />
        <stop offset="50%"  stopColor="#22D3EE" />
        <stop offset="100%" stopColor="#34D399" />
      </linearGradient>

      {/* AI nodes gradient — lighter indigo → emerald */}
      <linearGradient id="oru-ai-nodes" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%"   stopColor="#818CF8" />
        <stop offset="100%" stopColor="#34D399" />
      </linearGradient>

      {/* Subtle inner glow for the center pulse */}
      <radialGradient id="oru-ai-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor="#ffffff" stopOpacity="1" />
        <stop offset="100%" stopColor="#22D3EE" stopOpacity="0.6" />
      </radialGradient>
    </defs>

    {/* ── Background ── */}
    <circle cx="100" cy="100" r="84" fill="#0B1220" />

    {/* ── Gradient ring (brand signature) ── */}
    <circle cx="100" cy="100" r="78" fill="url(#oru-ai-ring)" opacity="0.92" />

    {/* ── Inner dark fill ── */}
    <circle cx="100" cy="100" r="66" fill="#0B1220" />

    {/* ── Brand C-arc ── */}
    <path
      d="M 138 64 A 52 52 0 1 0 138 136"
      stroke="white"
      strokeWidth="11"
      strokeLinecap="round"
      fill="none"
    />

    {/* ── Brand dot + halo ── */}
    <circle cx="142" cy="100" r="6"  fill="white" />
    <circle cx="142" cy="100" r="12" stroke="white" strokeOpacity="0.32" strokeWidth="2" />

    {/* ── AI neural nodes (triangle) ── */}
    {/* Top node */}
    <circle cx="98"  cy="68"  r="8.5" fill="url(#oru-ai-nodes)" />
    {/* Bottom-left node */}
    <circle cx="75"  cy="117" r="8.5" fill="url(#oru-ai-nodes)" />
    {/* Bottom-right node */}
    <circle cx="121" cy="117" r="8.5" fill="url(#oru-ai-nodes)" />

    {/* ── Neural connection lines ── */}
    <line x1="98"  y1="68"  x2="75"  y2="117" stroke="url(#oru-ai-nodes)" strokeWidth="2.5" strokeOpacity="0.6" />
    <line x1="98"  y1="68"  x2="121" y2="117" stroke="url(#oru-ai-nodes)" strokeWidth="2.5" strokeOpacity="0.6" />
    <line x1="75"  y1="117" x2="121" y2="117" stroke="url(#oru-ai-nodes)" strokeWidth="2.5" strokeOpacity="0.6" />

    {/* ── Center pulse dot (AI brain core) ── */}
    <circle cx="98" cy="98" r="5" fill="url(#oru-ai-glow)" />

    {/* ── Ambient accent sparkles ── */}
    <circle cx="60" cy="75"  r="2.5" fill="#22D3EE" opacity="0.72" />
    <circle cx="56" cy="108" r="2"   fill="#34D399" opacity="0.60" />
    <circle cx="67" cy="131" r="1.5" fill="#6366F1" opacity="0.50" />
  </svg>
);

export default OruclicksAIIcon;
